import { geoCentroid } from "d3-geo";
import { feature } from "topojson-client";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { SIGUNGU_TOPOLOGY } from "./succession";

type RegionProperties = {
  sigunguCode: string;
  sidoCode: string;
  name: string;
};

const regionCollection = feature(
  SIGUNGU_TOPOLOGY as never,
  SIGUNGU_TOPOLOGY.objects.sigungu as never,
) as unknown as FeatureCollection<Geometry, RegionProperties>;

function validCentroid(featureLike: Feature<Geometry, RegionProperties> | FeatureCollection): readonly [number, number] {
  const [lon, lat] = geoCentroid(featureLike);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    throw new Error("administrative geometry has no finite centroid");
  }
  return Object.freeze([lon, lat] as const);
}

export const SIGUNGU_CENTROIDS: ReadonlyMap<string, readonly [number, number]> = new Map(
  regionCollection.features
    .map((region) => [region.properties.sigunguCode, validCentroid(region)] as const)
    .sort(([a], [b]) => a.localeCompare(b)),
);

const featuresBySido = new Map<string, Array<Feature<Geometry, RegionProperties>>>();
for (const region of regionCollection.features) {
  const list = featuresBySido.get(region.properties.sidoCode) ?? [];
  list.push(region);
  featuresBySido.set(region.properties.sidoCode, list);
}

export const SIDO_CENTROIDS: ReadonlyMap<string, readonly [number, number]> = new Map(
  [...featuresBySido]
    .map(([sidoCode, features]) => [
      sidoCode,
      validCentroid({ type: "FeatureCollection", features }),
    ] as const)
    .sort(([a], [b]) => a.localeCompare(b)),
);

if (SIGUNGU_CENTROIDS.size !== 230 || SIDO_CENTROIDS.size !== 16) {
  throw new Error(
    `administrative centroids must contain 230 sigungu and 16 sido, got ${SIGUNGU_CENTROIDS.size}/${SIDO_CENTROIDS.size}`,
  );
}

export function getSigunguCentroid(code: string): readonly [number, number] {
  const centroid = SIGUNGU_CENTROIDS.get(code);
  if (!centroid) throw new Error(`unknown current sigungu code: ${code}`);
  return centroid;
}

export function getSidoCentroid(code: string): readonly [number, number] {
  const centroid = SIDO_CENTROIDS.get(code);
  if (!centroid) throw new Error(`unknown current sido code: ${code}`);
  return centroid;
}
